import {CustomSectionTypes, TeaserTypes} from '~/src/utilities/constants/types';
import {getFeedUrl} from '~/src/utilities/helpers/feed';
import {SectionLayout} from '~/src/utilities/constants/layout';
import {SiteArea} from '../constants/navigation';
import {createFormat} from '../feed/format';
import {TeaserFormat} from '../constants/formats';
import {getLifestyleFrontFormat, getLifestyleChannelFormat} from './lifestyle';
import {Teaser} from './teaser';
import {parseQueryString} from '~/src/utilities/helpers/string';

interface Section {
    type: 'section';
    subtype: 'default' | 'category' | 'custom';
    showTitle: boolean;
    sortID?: number;
    title: string;
    fullUrlPath?: string;
    layout?: string;
    pointerCategoryID?: number;
    customTypeIdentifier?: string;
}

interface FeedSection extends Section {
    sortID: number;
}

export interface TeaserSection extends Section {
    elements: Teaser[];
}

export interface CustomSection extends TeaserSection {
    subtype: 'custom';
    customTypeIdentifier: string;
    customType?: string;
    options?: {
        [key: string]: string;
    };
    fetchAdditionalData?: boolean;
    url?: boolean;
}

export const isCustomSection = (section: TeaserSection | CustomSection): section is CustomSection => section.subtype === 'custom';

const noAds = ({type, adUnitName}: Partial<Teaser>) => type !== 'ad' || adUnitName;
const noAdsOrSlider = ({type, adUnitName}: Partial<Teaser>) => noAds({type, adUnitName}) && type !== 'slider';

type AllowedGroupingInput = FeedSection | Teaser;
const rearrangeSection = (input: AllowedGroupingInput[], category: string): AllowedGroupingInput[] => {
    const categoryId = parseInt(category, 10);
    const start = input.findIndex((item) => item.type === 'section' && item.pointerCategoryID === categoryId);
    const end = input.findIndex((item, index) => index > start && item.type === 'section');

    return input.slice(start, end !== -1 ? end : input.length);
};
const rearrangeSections = (input: AllowedGroupingInput[]): AllowedGroupingInput[] => {
    const output = [];
    let currentSection;

    for (let i = 0; i < input.length; i++) {
        if (output.indexOf(input[i]) !== -1) {
            continue;
        }

        const element = input[i];

        currentSection = element.type === 'section' ? element : currentSection;

        if (element.type !== 'custom') {
            output.push(element);
            continue;
        }

        if (!element.identifier) {
            continue;
        }

        const [, params = ''] = element.identifier.split('?');
        const config = parseQueryString(params);

        if (!config.category) {
            continue;
        }

        output.push(...rearrangeSection(input, config.category));

        if (currentSection && input[i + 1] && input[i + 1].type !== 'section') {
            output.push({...currentSection});
        }
    }

    return output;
};

export const groupSections = (input: AllowedGroupingInput[]): TeaserSection[] => {
    const elements = rearrangeSections(input);
    const sections = elements.filter((element): element is FeedSection => element.type === 'section');

    return sections.map((section, index, allSections) => {
        const firstIndex = elements.indexOf(section) + 1;
        const lastIndex = elements.indexOf(allSections[index + 1]);

        return {
            ...section,
            elements: elements.slice(firstIndex, lastIndex !== -1 ? lastIndex : elements.length) as Teaser[]
        };
    });
};

/**
 * parse and transform any custom section
 *
 * @param section teaser section
 */
export const transformCustomSection = (section: CustomSection) => {
    const [type, params = ''] = section.customTypeIdentifier.split('?');

    section.customType = type;

    switch (type) {
        case CustomSectionTypes.LIFESTYLE_QUOTE:
            section.options = {
                pos: '1',
                theme: '1',
                ...parseQueryString(params)
            };
            section.showTitle = false;
            section.fetchAdditionalData = true;
            section.url = getFeedUrl('content', {metadataId: section.options.id});
            break;
        case CustomSectionTypes.MOST_POPULAR:
            section.options = parseQueryString(params);
            section.showTitle = section.options.title === 'true';
            section.fetchAdditionalData = true;
            break;
        default:
            break;
    }
};

/**
 * reorder teasers in any section according to their forced position
 *
 * @param section teaser section
 */
export const reorderSection = (section: TeaserSection) => {
    const size = section.elements.length;
    const forcedPositionElements = section.elements.filter((element) => element.forcePosition);
    section.elements = section.elements.filter((element) => !element.forcePosition);

    forcedPositionElements.forEach((element) => {
        let forcedPosition = parseInt(element.forcePosition || '', 10);
        if (forcedPosition) {
            forcedPosition = Math.min(forcedPosition, size);
            forcedPosition -= 1;
            forcedPosition = Math.max(forcedPosition, 0);
            forcedPosition += section.elements.filter(({type, isCrassAd}, index) => index < forcedPosition && type === TeaserTypes.AD && !isCrassAd).length;

            section.elements.splice(forcedPosition, 0, element);
        }
    });
};

/**
 * Go through all elements of a teaser section and set the correct sortID.
 * If it's an ad (non paid-post) we skip the element.
 *
 * @param start initial value
 * @param section teaser section
 */
export const numberSection = (section: TeaserSection, start = 0) =>
    section.elements.filter(noAdsOrSlider).reduce((sortID, element) => {
        element.sortID = sortID;

        return sortID + 1;
    }, start);

/**
 * Set all teaser formats to small
 *
 * @param section teaser section
 */
export const formatSmallSection = (section: TeaserSection) =>
    section.elements.filter(noAds).forEach((element) => (element.format = createFormat(TeaserFormat.TEASER_SMALL)));

/**
 * Set the first and second teaser format to large, the rest to small
 *
 * @param section teaser section
 */
export const formatVideoSection = (section: TeaserSection) =>
    section.elements
        .filter(noAds)
        .forEach((element) => (element.format = createFormat(element.sortID <= 1 ? TeaserFormat.TEASER_LARGE : TeaserFormat.TEASER_SMALL)));

/**
 * Set teaser format according to lifestyle front design
 *
 * @param section teaser section
 * @param sectionIndex index in the list of sections
 */
export const formatLifestyleFrontSection = (section: TeaserSection, sectionIndex: number) =>
    section.elements
        .filter(noAds)
        .forEach((element, index, teasers) => (element.format = getLifestyleFrontFormat(element, index, sectionIndex, teasers.length)));

/**
 * Set teaser format according to lifestyle channel design
 *
 * @param section teaser section
 * @param sectionIndex index in the list of sections
 */
export const formatLifestyleChannelSection = (section: TeaserSection, sectionIndex: number) =>
    section.elements
        .filter(noAds)
        .forEach((element, index, teasers) => (element.format = getLifestyleChannelFormat(element, index, sectionIndex, teasers.length)));

/**
 * Set teaser format to default logic (4n)
 *
 * @param section teaser section
 */
export const formatDefaultSection = (section: TeaserSection) =>
    section.elements
        .filter(noAds)
        .forEach((element) => (element.format = createFormat(element.sortID % 4 === 0 ? TeaserFormat.TEASER_LARGE : TeaserFormat.TEASER_SMALL)));

/**
 * Transform all custom teaser sections
 *
 * @param sections teaser sections
 */
export const transformCustomSections = (sections: TeaserSection[]) => sections.filter(isCustomSection).forEach(transformCustomSection);

/**
 * Reorder teasers in all teaser sections
 *
 * @param sections teaser sections
 */
export const reorderSections = (sections: TeaserSection[]) => sections.forEach(reorderSection);

/**
 * Set SortID for teasers in all teaser sections
 *
 * @param sections teaser sections
 */
export const numberSections = (sections: TeaserSection[]) => {
    sections.forEach((section, index) => (section.sortID = index));

    sections
        .filter(({subtype}) => subtype !== 'custom')
        .reduce(
            (start, section, index, filteredSections) =>
                numberSection(
                    section,
                    section.subtype === 'default' && section.pointerCategoryID === filteredSections[index - 1]?.pointerCategoryID ? start : 0
                ),
            0
        );
};

/**
 * Set the right teaser for all teaser sections
 *
 * @param {array} sections all teaser sections to process
 * @param {string} siteArea name of the siteArea used for teaser formats
 * @param {object} data full data
 */
export const formatSections = (sections: TeaserSection[], siteArea: string, data: {content: {fullUrlPath: string}}) => {
    sections.forEach((section, index) => {
        switch (section.layout) {
            case SectionLayout.CAROUSEL:
            case SectionLayout.RELATED_CONTENT:
                formatSmallSection(section);
                break;
            default:
                switch (siteArea) {
                    case SiteArea.VIDEO:
                        formatVideoSection(section);
                        break;
                    case SiteArea.LIFESTYLE:
                        if (data.content.fullUrlPath === 'lifestyle/front') {
                            formatLifestyleFrontSection(section, index);
                        } else {
                            formatLifestyleChannelSection(section, index);
                        }
                        break;
                    default:
                        formatDefaultSection(section);
                        break;
                }
                break;
        }
    });
};
